import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { expireSession } from "../store/reducers/sessionSlice";
import type { RootState } from "../store/store";

/**
 * Longest single wait. setTimeout overflows above 2^31-1 ms (~24.8 days) and fires at once,
 * while a session now slides to 90 days on every validate-session (MED-1621): wait in steps
 * and check the server expiry again at the end of each one.
 */
export const MAX_EXPIRATION_CHECK_DELAY_MS = 24 * 60 * 60 * 1000;

/**
 * Ends the session in the app only once the server expiry (`expiresAt` of validate-session) is
 * really reached. Inactivity no longer logs out: it locks the screen (PIN, MED-1621).
 */
export const useSessionExpiration = () => {
  const dispatch = useDispatch();
  const expiresAt = useSelector((state: RootState) => state.session.expiresAt);
  const sessionStatus = useSelector(
    (state: RootState) => state.session.sessionStatus
  );

  useEffect(() => {
    if (sessionStatus !== "VALID_SESSION" || !expiresAt) {
      return;
    }

    const expirationTime = new Date(expiresAt).getTime();
    if (Number.isNaN(expirationTime)) {
      return;
    }

    let timer: number | undefined;
    const check = () => {
      const remaining = expirationTime - Date.now();
      if (remaining <= 0) {
        dispatch(expireSession());
        return;
      }
      timer = window.setTimeout(
        check,
        Math.min(remaining, MAX_EXPIRATION_CHECK_DELAY_MS)
      );
    };
    check();

    return () => {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    };
  }, [expiresAt, sessionStatus, dispatch]);
};
